import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Portfolio: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const filters = [
    { id: 'all', name: 'Tümü' },
    { id: 'urban', name: 'Şehir' },
    { id: 'nature', name: 'Doğa' },
    { id: 'events', name: 'Etkinlikler' },
  ];

  const portfolioItems = [
    {
      id: 1,
      title: 'Ankara Kalesi',
      category: 'urban',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop',
      description: "Ankara Kalesi'nin muhteşem manzarası",
    },
    {
      id: 2,
      title: 'Kızılay Meydanı',
      category: 'urban',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=800&fit=crop',
      description: "Kızılay Meydanı'nın havadan görünümü",
    },
    {
      id: 3,
      title: 'Atakule',
      category: 'urban',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=500&fit=crop',
      description: "Atakule'nin gece çekimi",
    },
    {
      id: 4,
      title: 'Beypazarı Vadisi',
      category: 'nature',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=700&fit=crop',
      description: "Beypazarı'nın doğal güzellikleri",
    },
    {
      id: 5,
      title: 'Gölbaşı Gölü',
      category: 'nature',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop',
      description: "Gölbaşı Gölü'nün panoramik görünümü",
    },
    {
      id: 6,
      title: 'Düğün Çekimi',
      category: 'events',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=900&fit=crop',
      description: 'Özel düğün çekimi',
    },
    {
      id: 7,
      title: 'Kurumsal Etkinlik',
      category: 'events',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=550&fit=crop',
      description: 'Kurumsal etkinlik çekimi',
    },
    {
      id: 8,
      title: 'Anıtkabir',
      category: 'urban',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=650&fit=crop',
      description: "Anıtkabir'in havadan görünümü",
    },
    {
      id: 9,
      title: 'Eymir Gölü',
      category: 'nature',
      image:
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=750&fit=crop',
      description: "Eymir Gölü'nün doğal manzarası",
    },
  ];

  const filteredItems =
    selectedFilter === 'all'
      ? portfolioItems
      : portfolioItems.filter((item) => item.category === selectedFilter);

  const handleImageClick = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % filteredItems.length);
    }
  };

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(
        selectedImage === 0 ? filteredItems.length - 1 : selectedImage - 1
      );
    }
  };

  useEffect(() => {
    document.title = 'Portfolyo - Yüksekkare';
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-navy to-blue-900 text-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Portfolyo
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Çalışmalarımızdan örnekler. Her proje özel ve benzersizdir.
          </motion.p>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="section-padding bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filters.map((filter) => (
              <motion.button
                key={filter.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedFilter(filter.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  selectedFilter === filter.id
                    ? 'bg-primary-orange text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {filter.name}
              </motion.button>
            ))}
          </div>

          {/* Portfolio Grid */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="mb-6 break-inside-avoid"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="card p-0 overflow-hidden cursor-pointer group"
                  onClick={() => handleImageClick(index)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="text-white text-center">
                        <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                        <p className="text-sm">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors"
              >
                <X size={32} />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors"
              >
                <ChevronLeft size={32} />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors"
              >
                <ChevronRight size={32} />
              </button>

              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={filteredItems[selectedImage].image}
                  alt={filteredItems[selectedImage].title}
                  className="max-w-full max-h-[80vh] object-contain"
                />
                <div className="text-center mt-4 text-white">
                  <h3 className="text-2xl font-bold mb-2">
                    {filteredItems[selectedImage].title}
                  </h3>
                  <p className="text-gray-300">
                    {filteredItems[selectedImage].description}
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Portfolio;
